import { useState, useEffect, useCallback } from 'react'
import { Box, Flex, Text, VStack, Grid, Modal, ConfirmDialog, useT } from '@minetrace/ui'
import { useFormColors, TrackedInput, TrackedDatePicker, TrackedSelect, FileInput, withFormProvider } from '@minetrace/ui/forms'
import { PRODUCTION_PERIODS, enumLabel } from '../../../data/enums'
import {
  LuChevronDown as ChevronDown,
  LuChevronRight as ChevronRight,
  LuChartBar as BarChart,
  LuPlus as Plus,
  LuTrash2 as Trash2,
} from 'react-icons/lu'
import type { MiningRightResponse } from '../../../api/models/response/proveedor'
import MiningRightDataWarning from '../../../components/MiningRightDataWarning'
import type { AddProductionDeclarationRequest } from '../../../api/models/request/proveedor'
import { useStep5ProductionForm, type Step5ProductionFormValues } from './useStep5ProductionForm'
import { useDeleteWarning } from '../../../lib/modificationMode'

// ── Modal Form ──

interface ProductionFormInnerProps {
  formMethods: ReturnType<typeof useStep5ProductionForm>
  isBusy: boolean
  onSave: (values: Step5ProductionFormValues, documentFile?: File) => void
  onRegisterSubmit?: (fn: () => void) => void
}

const ProductionFormInner = withFormProvider<Step5ProductionFormValues, ProductionFormInnerProps>(
  ({ formMethods, isBusy, onSave, onRegisterSubmit }) => {
    const [documentFile, setDocumentFile] = useState<File | null>(null)

    const handleFormSubmit = useCallback(async () => {
      const isValid = await formMethods.trigger()
      if (!isValid) return
      onSave(formMethods.getValues(), documentFile ?? undefined)
    }, [formMethods, onSave, documentFile])

    useEffect(() => {
      onRegisterSubmit?.(() => handleFormSubmit())
    }, [onRegisterSubmit, handleFormSubmit])

    const today = new Date().toISOString().split('T')[0]

    return (
      <Box opacity={isBusy ? 0.5 : 1} pointerEvents={isBusy ? 'none' : 'auto'} transition="opacity 0.2s">
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
          <TrackedSelect name="period" labelTid="proveedores.wizard.step5.periodLabel" options={[...PRODUCTION_PERIODS]} placeholderTid="proveedores.wizard.step5.selectPlaceholder" />
          <TrackedInput name="year" labelTid="proveedores.wizard.step5.yearLabel" type="number" placeholderTid="proveedores.wizard.step5.yearPlaceholder" />
          <TrackedInput name="fileNumber" labelTid="proveedores.wizard.step5.fileNumberLabel" placeholderTid="proveedores.wizard.step5.fileNumberPlaceholder" maxLength={100} />
          <TrackedDatePicker name="presentationDate" labelTid="proveedores.wizard.step5.presentationDateLabel" maxDate={today} />
          {/* Estaba en el esquema y se enviaba al backend, pero no habia campo:
              el usuario no podia cargarlo y la ficha lo mostraba siempre vacio. */}
          <TrackedInput name="dailyEstimatedProduction" labelTid="proveedores.wizard.step5.dailyEstimatedProductionLabel" type="number" placeholderTid="proveedores.wizard.step5.numberPlaceholder" />
          <TrackedInput name="totalMetallic" labelTid="proveedores.wizard.step5.totalMetallicLabel" type="number" placeholderTid="proveedores.wizard.step5.numberPlaceholder" />
          <TrackedInput name="totalNonMetallic" labelTid="proveedores.wizard.step5.totalNonMetallicLabel" type="number" placeholderTid="proveedores.wizard.step5.numberPlaceholder" />
        </Grid>

        <Grid templateColumns="1fr" gap={4} mt={4}>
          <FileInput
            labelTid="proveedores.wizard.step5.documentLabel"
            value={documentFile}
            onChange={setDocumentFile}
            accept=".pdf"
            maxSizeMb={5}
          />
        </Grid>
      </Box>
    )
  },
  useStep5ProductionForm,
)

// ── Main Component ──

interface Step5ProductionProps {
  mode: 'edit' | 'readonly'
  companyId: string | null
  savedMiningRights: MiningRightResponse[]
  onAdd: (mrId: string, data: AddProductionDeclarationRequest, documentFile?: File) => Promise<void>
  onRemove: (mrId: string, declId: string) => Promise<void>
}

export default function Step5Production({ mode, savedMiningRights, onAdd, onRemove }: Step5ProductionProps) {
  const c = useFormColors()
  const { t } = useT()
  const isReadonly = mode === 'readonly'

  const [expandedRights, setExpandedRights] = useState<Set<string>>(new Set())
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMrId, setModalMrId] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [submitFn, setSubmitFn] = useState<(() => void) | null>(null)
  const deleteWarning = useDeleteWarning()
  const [deleteTarget, setDeleteTarget] = useState<{ mrId: string; declId: string; period: string } | null>(null)

  const toggleRight = (id: string) => {
    setExpandedRights(prev => { const next = new Set(prev); if (next.has(id)) { next.delete(id) } else { next.add(id) }; return next })
  }

  const openModal = (mrId: string) => { setModalMrId(mrId); setModalOpen(true) }
  const closeModal = () => { setModalOpen(false); setModalMrId(null) }

  const handleSave = async (values: Step5ProductionFormValues, documentFile?: File) => {
    if (!modalMrId) return
    const data: AddProductionDeclarationRequest = {
      period: values.period,
      year: values.year,
      dailyEstimatedProduction: values.dailyEstimatedProduction,
      presentationDate: values.presentationDate || undefined,
      fileNumber: values.fileNumber || undefined,
      totalMetallic: values.totalMetallic,
      totalNonMetallic: values.totalNonMetallic,
      expirationDate: values.expirationDate || undefined,
    }
    setIsBusy(true)
    try { await onAdd(modalMrId, data, documentFile); closeModal() } finally { setIsBusy(false) }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    setIsBusy(true)
    try { await onRemove(deleteTarget.mrId, deleteTarget.declId); setDeleteTarget(null) } finally { setIsBusy(false) }
  }

  return (
    <VStack gap={6} alignItems="stretch">
      {savedMiningRights.length === 0 ? (
        <Box p={8} textAlign="center" borderRadius="xl" border="1px dashed" borderColor="gray.200">
          <BarChart size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <Text tid="proveedores.wizard.step5.emptyMessage" fontSize="sm" color={c.textMuted} />
        </Box>
      ) : (
        <VStack gap={4} alignItems="stretch">
          {savedMiningRights.map(right => {
            const isExpanded = expandedRights.has(right.id)
            const declarations = right.productionDeclarations ?? []

            return (
              <Box key={right.id} borderRadius="xl" border="1px solid" borderColor={c.border} overflow="hidden">
                <Flex px={5} py={3} bg={c.bgHover} alignItems="center" justifyContent="space-between" cursor="pointer" onClick={() => toggleRight(right.id)}>
                  <Flex alignItems="center" gap={2}>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <Text fontSize="sm" fontWeight="600" color={c.text}>{t('common.miningRightLabel')}: {right.name || right.uniqueCode}</Text>
                    <Text fontSize="xs" color={c.textMuted}>({right.uniqueCode})</Text>
                  </Flex>
                  <Flex alignItems="center" gap={3}>
                    <MiningRightDataWarning hasBoundary={right.hasBoundary} componentCount={right.components?.length ?? 0} />
                    <Text tid="proveedores.wizard.step5.declarationsCount" values={{ count: declarations.length }} fontSize="xs" color={c.textMuted} />
                  </Flex>
                </Flex>

                {isExpanded && (
                  <Box px={5} py={4}>
                    {!isReadonly && (
                      <Flex mb={4}>
                        <Box as="button" type="button" display="flex" alignItems="center" gap={2} px={4} py={2} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600" cursor="pointer" _hover={{ opacity: 0.9 }} onClick={() => openModal(right.id)}>
                          <Plus size={14} />{t('proveedores.wizard.step5.addButton')}
                        </Box>
                      </Flex>
                    )}

                    {declarations.length > 0 ? (
                      <Box overflowX="auto">
                        <Box as="table" w="100%" fontSize="sm">
                          <Box as="thead">
                            <Box as="tr">
                              {[t('proveedores.wizard.step5.colPeriod'), t('proveedores.wizard.step5.colYear'), t('proveedores.wizard.step5.colDate'), t('proveedores.wizard.step5.colFile'), t('proveedores.wizard.step5.colMetallic'), t('proveedores.wizard.step5.colNonMetallic'), ...(isReadonly ? [] : [''])].map(col => (
                                <Box as="th" key={col} textAlign="left" px={3} py={2} color={c.textMuted} fontWeight="600" fontSize="xs" borderBottom="1px solid" borderColor={c.border}>{col}</Box>
                              ))}
                            </Box>
                          </Box>
                          <Box as="tbody">
                            {declarations.map(decl => (
                              <Box as="tr" key={decl.id} _hover={{ bg: c.bgHover }}>
                                <Box as="td" px={3} py={2} color={c.text}>{enumLabel(PRODUCTION_PERIODS, decl.period)}</Box>
                                <Box as="td" px={3} py={2} color={c.text}>{decl.year ?? '—'}</Box>
                                <Box as="td" px={3} py={2} color={c.text}>{decl.presentationDate ?? '—'}</Box>
                                <Box as="td" px={3} py={2} color={c.text}>{decl.fileNumber ?? '—'}</Box>
                                <Box as="td" px={3} py={2} color={c.text}>{decl.totalMetallic ?? '—'}</Box>
                                <Box as="td" px={3} py={2} color={c.text}>{decl.totalNonMetallic ?? '—'}</Box>
                                {!isReadonly && (
                                  <Box as="td" px={3} py={2}>
                                    <Box as="button" type="button" display="flex" alignItems="center" gap={1} px={3} py={1.5} bg="red.50" color="red.600" borderRadius="lg" fontSize="xs" fontWeight="600" cursor="pointer" _hover={{ bg: 'red.100' }} onClick={() => setDeleteTarget({ mrId: right.id, declId: decl.id, period: `${enumLabel(PRODUCTION_PERIODS, decl.period)} ${decl.year ?? ''}`.trim() || t('proveedores.wizard.step5.defaultPeriodName') })}>
                                      <Trash2 size={12} />{t('proveedores.wizard.step5.delete')}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Text tid="proveedores.wizard.step5.noDeclarations" fontSize="sm" color={c.textMuted} textAlign="center" py={4} />
                    )}
                  </Box>
                )}
              </Box>
            )
          })}
        </VStack>
      )}

      {/* Modal */}
      <Modal
        open={modalOpen} onClose={closeModal}
        title={t('proveedores.wizard.step5.modalTitle')}
        subtitle={modalMrId ? savedMiningRights.find(r => r.id === modalMrId)?.name : undefined}
        size="lg"
        footer={
          <>
            <Box as="button" type="button" px={5} py={2.5} borderRadius="md" fontSize="sm" fontWeight="600" color={c.textMuted} border="1px solid" borderColor={c.border} cursor="pointer" _hover={{ bg: c.bgHover }} onClick={closeModal}>{t('proveedores.wizard.step5.cancel')}</Box>
            <Box as="button" type="button" display="flex" alignItems="center" gap={2} px={6} py={2.5} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600" cursor={isBusy ? 'not-allowed' : 'pointer'} opacity={isBusy ? 0.6 : 1} _hover={{ opacity: 0.9 }} onClick={() => submitFn?.()} disabled={isBusy}>
              {isBusy ? t('proveedores.wizard.step5.saving') : <><Plus size={14} /> {t('proveedores.wizard.step5.register')}</>}
            </Box>
          </>
        }
      >
        <ProductionFormInner
          key={modalMrId ?? 'new'}
          isBusy={isBusy}
          onSave={handleSave}
          onRegisterSubmit={(fn) => setSubmitFn(() => fn)}
        />
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t('proveedores.wizard.step5.deleteTitle')}
        confirmLabel={t('proveedores.wizard.step5.deleteConfirm')}
        variant="danger"
        isLoading={isBusy}
      >
        ¿Está seguro que desea eliminar la declaración <Text as="span" fontWeight="600">{deleteTarget?.period}</Text>? {deleteWarning}
      </ConfirmDialog>
    </VStack>
  )
}
